import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {BottomSheet} from '@vio/atlas/src/components/designSystem/BottomSheet'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {ButtonLink} from '@vio/atlas/src/components/designSystem/ButtonLink'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import minus from '@vio/atlas/src/utils/minus'
import {Text} from '@vio/atlas/Text'
import {Tooltip} from '@vio/atlas/Tooltip'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {AiPoweredReviewsCategoryCard} from '../AiPoweredReviewsCategoryCard'
import {AiPoweredReviewsFeedback} from '../AiPoweredReviewsFeedback'
import {aiReviewsMessages} from '../constants'
import {useAiPoweredInsight} from '../useAiPoweredInsight'
import {getSplitOverall} from '../utils'
import {AiPoweredReviewsSummaryLoading} from './AiPoweredReviewsSummaryLoading'

import type {Hotel} from '@findhotel/sapi'

const CategoriesContainer = styled.div`
  display: flex;
  gap: ${cssTheme.layout.spacing.s300};
  margin-top: ${cssTheme.layout.spacing.s300};
  margin-left: ${minus(cssTheme.layout.spacing.s500)};
  margin-right: ${minus(cssTheme.layout.spacing.s500)};
  padding-left: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s500};
  margin-bottom: ${cssTheme.layout.spacing.s400};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledButtonLink = styled(ButtonLink)`
  padding: 0;
`

const IconWrapper = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-left: ${cssTheme.layout.spacing.s100};
`

interface Props {
  hotelId: Hotel['objectID']
  isCompact?: boolean
  onReadMoreClick: () => void
  onCategoryClick?: (category: string) => void
}
export const AiPoweredReviewsSummary: React.FC<Props> = ({
  hotelId,
  isCompact = false,
  onReadMoreClick,
  onCategoryClick
}) => {
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()
  const dispatchTeamEvent = useDispatchTeamEvent()

  const {data, isLoading} = useAiPoweredInsight(hotelId)

  const [isInsightModalOpen, setIsInsightModalOpen] = useState(false)

  const handleClickReadMore = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.AiReviewSummarySection,
        action: Action.Clicked,
        payload: {selectedElement: 'ReadMore'}
      })
    )
    onReadMoreClick()
  }

  const handleCategoryClick = (category: string) => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.AiReviewSummarySection,
        action: Action.Clicked,
        payload: {selectedElement: category}
      })
    )
    onCategoryClick?.(category)
  }

  if (isLoading) return <AiPoweredReviewsSummaryLoading />

  if (!data?.overall) return null

  const firstSentence = getSplitOverall(data?.overall)[0]

  if (isMobile) {
    return (
      <>
        <Box>
          <Box
            backgroundColor="background.brand.tertiary"
            padding="s600"
            borderRadius="lg"
          >
            <Box
              display="flex"
              gap="s200"
              alignItems="center"
              marginBottom="s100"
            >
              <Icon
                name="Sparkles"
                colorPath="content.brand.primary"
                size="lg"
              />

              <Text variant="titleL">
                {formatMessage(aiReviewsMessages.aiPoweredSummarySectionTitle)}
              </Text>
            </Box>
            <Box marginBottom="s400">
              <Text
                variant="bodyXS"
                colorPath="content.neutral.secondary"
                tag="div"
              >
                {formatMessage(
                  aiReviewsMessages.aiPoweredSummarySectionDescription
                )}
                <IconWrapper onClick={() => setIsInsightModalOpen(true)}>
                  <Icon
                    name="Information"
                    size="xs"
                    colorPath="content.neutral.secondary"
                  />
                </IconWrapper>
              </Text>
            </Box>
            <Text variant="bodyM" colorPath="content.neutral.primary">
              {firstSentence}
            </Text>
            <ButtonLink onClick={handleClickReadMore} style={{padding: 0}}>
              {isCompact ? (
                <FormattedMessageWrapper
                  id="reviews.readGuestReviews"
                  defaultMessage="Read guest reviews"
                />
              ) : (
                <FormattedMessageWrapper
                  defaultMessage="Read more"
                  id="readMore"
                />
              )}
            </ButtonLink>
          </Box>

          {!isCompact && (
            <CategoriesContainer>
              {data?.categorySummaries?.map(category => (
                <AiPoweredReviewsCategoryCard
                  hasPadding
                  key={category.category}
                  details={category}
                  onCategoryClick={() => handleCategoryClick(category.category)}
                />
              ))}
            </CategoriesContainer>
          )}
          {!isCompact && (
            <Box paddingTop="s400">
              <AiPoweredReviewsFeedback sourceComponent="Summary" />
            </Box>
          )}
        </Box>
        <BottomSheet
          isOpen={isInsightModalOpen}
          onClose={() => setIsInsightModalOpen(false)}
          navBarProps={{
            centerContent: (
              <Text variant="titleM">
                {formatMessage(aiReviewsMessages.aiPoweredReviewInsightsTitle)}
              </Text>
            )
          }}
        >
          {isInsightModalOpen && (
            <>
              <Box paddingTop="s300" paddingBottom="s500">
                <Text variant="bodyM">
                  {formatMessage(
                    aiReviewsMessages.aiPoweredReviewInsightsDescription
                  )}
                </Text>
              </Box>
              <Box paddingBottom="s300">
                <Button
                  variant="tertiary"
                  size="lg"
                  onClick={() => setIsInsightModalOpen(false)}
                  fullWidth
                >
                  <Text variant="labelM">
                    <FormattedMessageWrapper
                      id="gotItButton"
                      defaultMessage="Ok, got it"
                    />
                  </Text>
                </Button>
              </Box>
            </>
          )}
        </BottomSheet>
      </>
    )
  }

  return (
    <Box
      backgroundColor="background.brand.tertiary"
      padding="s600"
      borderRadius="lg"
    >
      <Box display="flex" gap="s200" alignItems="center" marginBottom="s100">
        <Icon name="Sparkles" colorPath="content.brand.primary" size="lg" />

        <Text variant="titleL">
          {formatMessage(aiReviewsMessages.aiPoweredSummarySectionTitle)}
        </Text>
      </Box>
      <Box display="flex" gap="s100" alignItems="center" marginBottom="s400">
        <Text variant="bodyXS" colorPath="content.neutral.secondary">
          {formatMessage(aiReviewsMessages.aiPoweredSummarySectionDescription)}
        </Text>
        <Tooltip
          title={formatMessage(aiReviewsMessages.aiPoweredReviewInsightsTitle)}
          icon={<Icon name="Sparkles" size="md" />}
          content={formatMessage(
            aiReviewsMessages.aiPoweredReviewInsightsDescription
          )}
        >
          <Icon
            name="Information"
            size="xs"
            colorPath="content.neutral.secondary"
          />
        </Tooltip>
      </Box>
      <Text variant="bodyM" colorPath="content.neutral.primary">
        {firstSentence}
      </Text>
      <StyledButtonLink onClick={handleClickReadMore}>
        {isCompact ? (
          <FormattedMessageWrapper
            id="reviews.readGuestReviews"
            defaultMessage="Read guest reviews"
          />
        ) : (
          <FormattedMessageWrapper defaultMessage="Read more" id="readMore" />
        )}
      </StyledButtonLink>

      {!isCompact && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          rowGap="s600"
          columnGap="s400"
          marginTop="s600"
        >
          {data.categorySummaries.map(category => (
            <AiPoweredReviewsCategoryCard
              key={category.category}
              details={category}
              onCategoryClick={() => handleCategoryClick(category.category)}
            />
          ))}
        </Box>
      )}
      {!isCompact && (
        <Box paddingTop="s600">
          <AiPoweredReviewsFeedback sourceComponent="Summary" />
        </Box>
      )}
    </Box>
  )
}
